import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Grid,
  GridItem,
  Card,
  CardBody,
  VStack,
  HStack,
  Avatar,
  Badge,
  Button,
  Icon,
  Progress,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Divider,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer
} from '@chakra-ui/react';
import {
  FaUserTie,
  FaUsers,
  FaBriefcase,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaChartLine,
  FaArrowRight
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import api from '../../../../services/api';

const GestorDetailCard = ({ gestor }) => {
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const navigate = useNavigate();

  const handleViewDetails = () => {
    navigate(`/diretoria-juridico/gestor/${gestor._id}`);
  };

  return (
    <Card bg={bgColor} borderWidth="1px" borderColor={borderColor}>
      <CardBody>
        <VStack align="stretch" spacing={4}>
          <HStack justify="space-between">
            <HStack spacing={3}>
              <Avatar name={gestor.name} size="md" bg="blue.500" />
              <VStack align="start" spacing={0}>
                <Text fontWeight="bold" fontSize="lg">{gestor.name}</Text>
                <Text fontSize="sm" color="gray.500">{gestor.email}</Text>
              </VStack>
            </HStack>
            <Badge colorScheme="purple" fontSize="sm">Gestor Jurídico</Badge>
          </HStack>

          <Divider />

          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <GridItem>
              <Stat>
                <StatLabel fontSize="xs">Equipe</StatLabel>
                <StatNumber fontSize="xl">{gestor.teamSize || 0}</StatNumber>
                <StatHelpText fontSize="xs">membros</StatHelpText>
              </Stat>
            </GridItem>
            <GridItem>
              <Stat>
                <StatLabel fontSize="xs">Produtividade</StatLabel>
                <StatNumber fontSize="xl">{gestor.productivity || 0}%</StatNumber>
                <StatHelpText fontSize="xs">
                  <StatArrow type={gestor.productivity > 70 ? 'increase' : 'decrease'} />
                </StatHelpText>
              </Stat>
            </GridItem>
          </Grid>

          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>Status da Equipe</Text>
            <VStack align="stretch" spacing={2}>
              <HStack justify="space-between">
                <HStack>
                  <Icon as={FaClock} color="yellow.500" boxSize={3} />
                  <Text fontSize="sm">Em Análise</Text>
                </HStack>
                <Text fontSize="sm" fontWeight="bold">{gestor.teamStats?.inProgress || 0}</Text>
              </HStack>
              <HStack justify="space-between">
                <HStack>
                  <Icon as={FaCheckCircle} color="green.500" boxSize={3} />
                  <Text fontSize="sm">Aprovados</Text>
                </HStack>
                <Text fontSize="sm" fontWeight="bold">{gestor.teamStats?.approved || 0}</Text>
              </HStack>
              <HStack justify="space-between">
                <HStack>
                  <Icon as={FaTimesCircle} color="red.500" boxSize={3} />
                  <Text fontSize="sm">Reprovados</Text>
                </HStack>
                <Text fontSize="sm" fontWeight="bold">{gestor.teamStats?.rejected || 0}</Text>
              </HStack>
            </VStack>
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={2}>Performance</Text>
            <Progress value={gestor.productivity || 0} colorScheme="blue" size="sm" />
          </Box>

          <Button
            colorScheme="blue"
            size="sm"
            rightIcon={<FaArrowRight />}
            onClick={handleViewDetails}
            variant="outline"
            width="full"
          >
            Ver Detalhes da Equipe
          </Button>
        </VStack>
      </CardBody>
    </Card>
  );
};

const TeamMemberRow = ({ member }) => {
  return (
    <Tr>
      <Td>
        <HStack>
          <Avatar name={member.name} size="sm" />
          <Text>{member.name}</Text>
        </HStack>
      </Td>
      <Td>{member.email}</Td>
      <Td>
        <Badge colorScheme="blue">Jurídico</Badge>
      </Td>
      <Td isNumeric>{member.assignments || 0}</Td>
      <Td isNumeric>{member.completed || 0}</Td>
      <Td>
        <Progress value={member.productivity || 0} size="sm" colorScheme="green" width="100px" />
      </Td>
    </Tr>
  );
};

const GestoresOverview = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gestores, setGestores] = useState([]);
  const [recentMembers, setRecentMembers] = useState([]);

  useEffect(() => {
    fetchGestoresData();
  }, []);

  const fetchGestoresData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/juridico/diretoria/gestores');
      const data = response.data || response;
      
      setGestores(Array.isArray(data) ? data : (data.gestores || []));
      
      // Get recent team members from all gestores
      const allMembers = [];
      (Array.isArray(data) ? data : (data.gestores || [])).forEach(gestor => {
        if (gestor.teamMembers) {
          allMembers.push(...gestor.teamMembers.slice(0, 2));
        }
      });
      setRecentMembers(allMembers.slice(0, 5));
    } catch (err) {
      console.error('Error fetching gestores data:', err);
      setError('Erro ao carregar dados dos gestores');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box py={10} textAlign="center">
        <Spinner size="xl" color="blue.500" />
        <Text mt={4}>Carregando gestores...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" rounded="md">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  const totalTeamSize = gestores.reduce((acc, g) => acc + (g.teamSize || 0), 0);
  const avgProductivity = gestores.length > 0 
    ? gestores.reduce((acc, g) => acc + (g.productivity || 0), 0) / gestores.length 
    : 0;

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <Box>
          <HStack spacing={3} mb={2}>
            <Icon as={FaUserTie} boxSize={6} color="purple.600" />
            <Heading size="lg">Gestores Jurídicos</Heading>
          </HStack>
          <Text color="gray.600">
            Gestão e performance das equipes jurídicas
          </Text>
        </Box>

        <Grid templateColumns="repeat(4, 1fr)" gap={4}>
          <GridItem>
            <Stat bg="white" p={4} rounded="lg" shadow="sm">
              <StatLabel>Total Gestores</StatLabel>
              <StatNumber>{gestores.length}</StatNumber>
            </Stat>
          </GridItem>
          <GridItem>
            <Stat bg="white" p={4} rounded="lg" shadow="sm">
              <StatLabel>Total Equipe</StatLabel>
              <StatNumber>{totalTeamSize}</StatNumber>
              <StatHelpText>analistas</StatHelpText>
            </Stat>
          </GridItem>
          <GridItem>
            <Stat bg="white" p={4} rounded="lg" shadow="sm">
              <StatLabel>Produtividade Média</StatLabel>
              <StatNumber>{Math.round(avgProductivity)}%</StatNumber>
            </Stat>
          </GridItem>
          <GridItem>
            <Stat bg="white" p={4} rounded="lg" shadow="sm">
              <StatLabel>Média por Gestor</StatLabel>
              <StatNumber>{Math.round(totalTeamSize / (gestores.length || 1))}</StatNumber>
              <StatHelpText>membros</StatHelpText>
            </Stat>
          </GridItem>
        </Grid>

        <Divider />

        <Box>
          <Heading size="md" mb={4}>Gestores</Heading>
          {gestores.length > 0 ? (
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              {gestores.map(gestor => (
                <GridItem key={gestor._id}>
                  <GestorDetailCard gestor={gestor} />
                </GridItem>
              ))}
            </Grid>
          ) : (
            <Alert status="info" rounded="md">
              <AlertIcon />
              Nenhum gestor encontrado.
            </Alert>
          )}
        </Box>

        {recentMembers.length > 0 && (
          <>
            <Divider />
            <Box>
              <Heading size="md" mb={4}>Membros Recentes das Equipes</Heading>
              <TableContainer bg="white" rounded="lg" shadow="sm">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Nome</Th>
                      <Th>Email</Th>
                      <Th>Cargo</Th>
                      <Th isNumeric>Designações</Th>
                      <Th isNumeric>Concluídas</Th>
                      <Th>Produtividade</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {recentMembers.map((member, index) => (
                      <TeamMemberRow key={member._id || index} member={member} />
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}
      </VStack>
    </Box>
  );
};

export default GestoresOverview;